import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { City } from "./city";
import { RootState } from "../../store";
import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";

interface CitySlice {
  cities: City[];
  formData: Partial<City>;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
}

const initialState: CitySlice = {
  cities: [],
  formData: { city_name: "", status: "active" },
  loading: false,
  error: null,
  pagination: null,
};

//get list
export const fetchCities = createAsyncThunk(
  "city/fetchCities",
  async (params?: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      const url = `/master/city${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await enhancedApiClient.get(url);
      if (isSuccessResponse(response)) {
        console.log("Fetch cities response:", response.data);
        return response.data;
      } else {
        return rejectWithValue("Failed to fetch cities");
      }
    } catch (error: any) {
      console.error("Fetch cities error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch cities";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Create State
export const createCity = createAsyncThunk(
  "city/createCity",
  async (data: Partial<City>, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.post("/master/city", data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to create city");
      }
    } catch (error: any) {
      console.error("Create city error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create city";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Edit State
export const editCity = createAsyncThunk(
  "city/editCity",
  async ({ id, data }: { id: string; data: Partial<City> }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.put(`/master/city/${id}`, data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to update city");
      }
    } catch (error: any) {
      console.error("Edit city error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update city";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Toggle city status
export const toggleCityStatus = createAsyncThunk(
  "city/toggleCityStatus",
  async ({ id, status }: { id: number; status: number }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.patch(`/master/city/${id}/status`, { status });

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to toggle city status");
      }
    } catch (error: any) {
      console.error("Toggle city status error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to toggle city status";
      return rejectWithValue(errorMessage);
    }
  }
);

export const citySlice = createSlice({
  name: "city",
  initialState,
  reducers: {
    setCityName: (state, action: PayloadAction<string>) => {
      state.formData.city_name = action.payload;
    },
    setCityStatus: (state, action: PayloadAction<string>) => {
      state.formData.status = action.payload;
    },
    resetForm: (state) => {
      state.formData = { city_name: "", status: "active" };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchCities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.loading = false;
        state.cities = action.payload.records.map((record: any) => ({
          id: record.cityId,
          cityId: record.cityId,
          city_name: record.cityName,
          state_id: record.stateId,
          state_name: record.stateName,   
          user_name: record.userName,       
          status: record.status,
          updated_at: record.updatedOn,
          updated_by: record.updatedBy,
          action: record.action,
        }));
        state.pagination = action.payload.pagination;
        console.log("States fetched successfully:", action.payload);
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createCity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCity.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const newCity: City = {
            id: action.payload.cityId,
            cityId: action.payload.cityId,
            city_name: action.payload.cityName,
            state_id: action.payload.stateId,
            state_name: action.payload.stateName,
            status: action.payload.status,
            updated_at: action.payload.updatedOn,
            updated_by: action.payload.updatedBy,
            action: action.payload.action,
          };
          state.cities.unshift(newCity); 
        }
        state.formData = { city_name: "", status: "active" };
      })
      .addCase(createCity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Edit
      .addCase(editCity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editCity.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) {
          const updatedCity: City = {
            id: action.payload.cityId,
            cityId: action.payload.cityId,
            city_name: action.payload.cityName,
            state_id: action.payload.stateId,
            state_name: action.payload.stateName,
            status: action.payload.status,
            updated_at: action.payload.updatedOn,
            updated_by: action.payload.updatedBy,
            action: action.payload.action,
          };
          const index = state.cities.findIndex(c => c.id === updatedCity.id);
          if (index !== -1) {
            state.cities[index] = updatedCity; 
          }
        }
        state.formData = { city_name: "", status: "active" };
      })
      .addCase(editCity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Toggle Status
    .addCase(toggleCityStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(toggleCityStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      const updateCity : City = {
        id: action.payload.cityId,
        city_name: action.payload.cityName,
        cityId: action.payload.cityId,
        state_id: action.payload.stateId,
        state_name: action.payload.stateName,
        status: action.payload.status,
        updated_at: action.payload.updatedOn,
        updated_by: action.payload.updatedBy,
        action: action.payload.action,
      }
      const index = state.cities.findIndex(s => s.id === updateCity.id);
      if (index !== -1) {
        state.cities[index] = updateCity;
      }
    })
    .addCase(toggleCityStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    })
  },
});

export const { setCityName, setCityStatus, resetForm } = citySlice.actions;
// ✅ Selectors
export const selectCities = (state: RootState) => state.city.cities;
export const selectCityLoading = (state: RootState) => state.city.loading;
export const selectCityError = (state: RootState) => state.city.error;
export const selectCityPagination = (state: RootState) => state.city.pagination

export default citySlice.reducer;
